/**
 * @file areas.api.js
 * @module AreasAPI
 * @description HTTP API wrapper for service areas endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios"

export const AreasAPI = {

    /**
     * Get paginated list of areas.
     * @param {number} limit - Number of items per page.
     * @param {number} page - Page number.
     * @param {string} query - Optional query string appended to the request.
     * @param {string} token - Optional auth token passed to axiosInstance.
     * @returns {Promise} Axios promise resolving to the areas list.
     */
    getAreas(limit, page, query, token = "") {
        return axiosInstance(token).get(`/areas?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Get a single area by its identifier.
     * @param {string} areaId - Area identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to the area data.
     */
    getAreaById(areaId, token = "") {
        return axiosInstance(token).get(`/areas/${areaId}`);
    },

    /**
     * Create a new area.
     * @param {Object} area - Area payload.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to the created area.
     */
    createArea(area, token = "") {
        return axiosInstance(token).post(`/areas`, area);
    },

    /**
     * Update an existing area by id.
     * @param {string} areaId - Area identifier.
     * @param {Object} area - Updated area payload.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to the updated area.
     */
    updateAreaById(areaId, area, token = "") {
        return axiosInstance(token).put(`/areas/${areaId}`, area);
    },

    /**
     * Delete an area by id.
     * @param {string} areaId - Area identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteAreaById(areaId, token = "") {
        return axiosInstance(token).delete(`/areas/${areaId}`);
    },

    /**
     * Delete many areas by providing an array of ids or objects.
     * @param {Array|Object} areas - Array or payload describing areas to delete.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteManyAreasById(areas, token = "") {
        return axiosInstance(token).post(`/bulk-delete-areas`, areas);
    },

    /**
     * Get areas belonging to a specific branch, paginated.
     * @param {string} branchId - Branch identifier.
     * @param {number} limit - Page size.
     * @param {number} page - Page number.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to branch areas.
     */
    getAreasByBranchId(branchId, limit, page, token = "") {
        return axiosInstance(token).get(`/branches/${branchId}/areas?limit=${limit}&page=${page}`)
    },

    /**
     * Get options (lightweight list) of areas for a branch.
     * @param {string} branchId - Branch identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to area options.
     */
    getAreasOptionsByBranchId(branchId, token = "") {
        return axiosInstance(token).get(`/branches/${branchId}/areas-options`)
    },

    /**
     * Get histories for a specific area.
     * @param {string} areaId - Area identifier.
     * @param {number} limit - Page size.
     * @param {number} page - Page number.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to history entries.
     */
    getHistoriesByAreaId: (areaId, limit, page, token = "") => {
        return axiosInstance(token).get(`/modules/areas/${areaId}/histories?limit=${limit}&page=${page}`)
    },

    /**
     * Update a history entry by id.
     * @param {string} historyId - History entry identifier.
     * @param {Object} history - Updated history payload.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to updated history.
     */
    updateHistoryById: (historyId, history, token = "") => {
        return axiosInstance(token).put(`/histories/${historyId}`, history)
    },
}